import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FolderOpen,
  Plus,
  FileText,
  Video,
  FileSpreadsheet,
  Link as LinkIcon,
  Download,
  ExternalLink,
  Search,
  Trash2,
  Pencil
} from 'lucide-react';

const ResourcesPage = () => {
  const { user, api, canModerate } = useAuth();
  const navigate = useNavigate();
  
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    category: 'general',
    resource_type: 'document',
    url: '',
    tags: ''
  });

  const categories = [
    { value: 'general', label: 'Général' },
    { value: 'formation', label: 'Formation' },
    { value: 'templates', label: 'Templates' },
    { value: 'replays', label: 'Replays' },
    { value: 'outils', label: 'Outils' },
    { value: 'juridique', label: 'Juridique' }
  ];

  const resourceTypes = [
    { value: 'document', label: 'Document', icon: <FileText className="w-4 h-4" /> },
    { value: 'video', label: 'Vidéo', icon: <Video className="w-4 h-4" /> },
    { value: 'template', label: 'Template', icon: <FileSpreadsheet className="w-4 h-4" /> },
    { value: 'link', label: 'Lien externe', icon: <LinkIcon className="w-4 h-4" /> }
  ];

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await api.get('/resources');
      setResources(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!newResource.title || !newResource.url) {
      toast.error('Titre et URL requis');
      return;
    }
    try {
      await api.post('/resources', {
        ...newResource,
        tags: newResource.tags ? newResource.tags.split(',').map(t => t.trim()) : []
      });
      toast.success('📚 Ressource ajoutée !');
      setCreateOpen(false);
      setNewResource({ title: '', description: '', category: 'general', resource_type: 'document', url: '', tags: '' });
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!confirm('Supprimer cette ressource ?')) return;
    try {
      await api.delete(`/resources/${resourceId}`);
      toast.success('Ressource supprimée');
      fetchResources();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditDialog = (resource) => {
    setEditingResource({
      ...resource,
      tags: Array.isArray(resource.tags) ? resource.tags.join(', ') : ''
    });
    setEditOpen(true);
  };

  const handleUpdateResource = async (e) => {
    e.preventDefault();
    if (!editingResource?.title || !editingResource?.url) {
      toast.error('Titre et URL requis');
      return;
    }
    try {
      const payload = {
        title: editingResource.title,
        description: editingResource.description || '',
        url: editingResource.url,
        category: editingResource.category,
        resource_type: editingResource.resource_type,
        tags: editingResource.tags
          ? editingResource.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : []
      };
      await api.patch(`/resources/${editingResource.id}`, payload);
      toast.success('Ressource mise à jour');
      setEditOpen(false);
      setEditingResource(null);
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const getTypeIcon = (type) => {
    const found = resourceTypes.find(t => t.value === type);
    return found?.icon || <FileText className="w-5 h-5" />;
  };

  const filteredResources = resources.filter(r => {
    const matchesFilter = filter === 'all' || r.category === filter;
    const matchesSearch = !searchQuery || 
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <FolderOpen className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des ressources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="resources-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Bibliothèque</h1>
                <p className="text-sm text-muted-foreground">Documents, templates et replays</p>
              </div>
            </div>
          </div>

          {canModerate && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-green-500 text-white hover:bg-green-600" data-testid="add-resource-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-heading">Ajouter une ressource</DialogTitle>
                  <DialogDescription>Partagez un document ou un lien avec la communauté</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateResource} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={newResource.resource_type} 
                        onValueChange={(v) => setNewResource({ ...newResource, resource_type: v })}
                      >
                        <SelectTrigger className="bg-secondary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {resourceTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                {type.icon}
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Catégorie</Label>
                      <Select 
                        value={newResource.category} 
                        onValueChange={(v) => setNewResource({ ...newResource, category: v })}
                      >
                        <SelectTrigger className="bg-secondary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input 
                      value={newResource.title} 
                      onChange={(e) => setNewResource({ ...newResource, title: e.target.value })} 
                      placeholder="Ex: Template de calcul de rentabilité" 
                      className="bg-secondary/50"
                      data-testid="resource-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL *</Label>
                    <Input 
                      value={newResource.url} 
                      onChange={(e) => setNewResource({ ...newResource, url: e.target.value })} 
                      placeholder="https://..." 
                      className="bg-secondary/50"
                      data-testid="resource-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={newResource.description} 
                      onChange={(e) => setNewResource({ ...newResource, description: e.target.value })} 
                      placeholder="Description de la ressource..."
                      className="bg-secondary/50"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tags (séparés par virgule)</Label>
                    <Input 
                      value={newResource.tags} 
                      onChange={(e) => setNewResource({ ...newResource, tags: e.target.value })} 
                      placeholder="excel, rentabilité, calcul" 
                      className="bg-secondary/50"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                    <Button type="submit" className="bg-green-500 text-white" data-testid="submit-resource-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une ressource..."
              className="pl-10 bg-secondary/50"
              data-testid="search-resources-input"
            />
          </div>
          
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-secondary/50 flex-wrap h-auto">
              <TabsTrigger value="all" data-testid="filter-tab-all">Tout</TabsTrigger>
              {categories.map((cat) => (
                <TabsTrigger key={cat.value} value={cat.value} data-testid={`filter-tab-${cat.value}`}>
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Resources Grid */}
        {filteredResources.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune ressource trouvée</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'Essayez avec d\'autres mots-clés' : 'Les ressources apparaîtront ici'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredResources.map((resource) => (
              <Card key={resource.id} className="card-base group" data-testid={`resource-${resource.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-green-500">
                      {getTypeIcon(resource.resource_type)}
                    </div>
                    {canModerate && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-[var(--gold,#B5894A)]/10 hover:text-[var(--gold,#B5894A)]"
                          onClick={() => openEditDialog(resource)}
                          data-testid={`edit-resource-${resource.id}`}
                          aria-label="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-red-500/10 hover:text-red-500"
                          onClick={() => handleDeleteResource(resource.id)}
                          data-testid={`delete-resource-${resource.id}`}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-base mt-2">{resource.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{resource.description || 'Pas de description'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <Badge variant="outline" className="text-xs">
                      {categories.find(c => c.value === resource.category)?.label || resource.category}
                    </Badge>
                    {resource.tags?.slice(0, 2).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/20"
                    onClick={() => window.open(resource.url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ouvrir
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Resource Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border" data-testid="edit-resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier la ressource</DialogTitle>
            <DialogDescription>Met à jour les informations de cette ressource</DialogDescription>
          </DialogHeader>
          {editingResource && (
            <form onSubmit={handleUpdateResource} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={editingResource.resource_type}
                    onValueChange={(v) => setEditingResource({ ...editingResource, resource_type: v })}
                  >
                    <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {resourceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">{type.icon}{type.label}</div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select
                    value={editingResource.category}
                    onValueChange={(v) => setEditingResource({ ...editingResource, category: v })}
                  >
                    <SelectTrigger className="bg-secondary/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={editingResource.title}
                  onChange={(e) => setEditingResource({ ...editingResource, title: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="edit-resource-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>URL *</Label>
                <Input
                  value={editingResource.url}
                  onChange={(e) => setEditingResource({ ...editingResource, url: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="edit-resource-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingResource.description || ''}
                  onChange={(e) => setEditingResource({ ...editingResource, description: e.target.value })}
                  className="bg-secondary/50"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (séparés par virgule)</Label>
                <Input
                  value={editingResource.tags}
                  onChange={(e) => setEditingResource({ ...editingResource, tags: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-green-500 text-white" data-testid="submit-edit-resource-btn">
                  Enregistrer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourcesPage;
